import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ShieldCheck, Settings, TrendingUp, Cog, Compass } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import ContactForm from "../components/ContactForm";
import Seo from "../components/Seo";

const ICONS = { ShieldCheck, Settings, TrendingUp, Cog, Compass };

export const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const els = ref.current?.querySelectorAll(".reveal") || [];
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((en) => en.isIntersecting && en.target.classList.add("is-visible")),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });
  return ref;
};

const Hero = () => {
  const { t, lp } = useLang();
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const slides = t.hero.slides;

  useEffect(() => {
    const timer = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden">
      {slides.map((src, i) => (
        <div key={src} className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}>
          <img src={src} alt="" className={`h-full w-full object-cover grayscale ${i === idx ? "animate-kenburns" : ""}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-[1200px] flex-col justify-center px-6">
        <div className="max-w-3xl">
          <h1 className="text-4xl font-extrabold leading-[1.1] text-white sm:text-5xl lg:text-[3.4rem]">{t.hero.title}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-200">{t.hero.subtitle}</p>
          <button
            onClick={() => navigate(lp("/contact"))}
            className="group mt-8 inline-flex items-center gap-2 rounded-md bg-[#F26522] px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-[#d9561a] hover:shadow-xl"
          >
            {t.hero.cta}
            <ChevronRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 gap-2.5">
        {slides.map((src, i) => (
          <button key={src} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
            className={`h-2.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-[#F26522]" : "w-2.5 bg-white/60 hover:bg-white"}`} />
        ))}
      </div>
    </section>
  );
};

const Home = () => {
  const ref = useReveal();
  const navigate = useNavigate();
  const { t, lp } = useLang();

  return (
    <div ref={ref}>
      <Seo title={t.seo.home.title} description={t.seo.home.description} />
      <Hero />

      <section className="mx-auto max-w-[1200px] px-6 py-20 lg:py-28">
        <div className="reveal">
          <span className="text-sm font-semibold uppercase tracking-widest text-[#F26522]">{t.intro.brand}</span>
          <h2 className="mt-3 max-w-4xl text-3xl font-bold leading-tight text-neutral-900 lg:text-4xl">{t.intro.headline}</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {t.intro.paragraphs.map((p) => (
              <p key={p.slice(0, 40)} className="text-[15px] leading-relaxed text-neutral-600">{p}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-neutral-50 py-20 lg:py-28">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="reveal mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-neutral-900 lg:text-4xl">{t.expertise.title}</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-neutral-600">{t.expertise.intro}</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {t.expertise.items.map((item, i) => {
              const Icon = ICONS[item.icon];
              const isLast = i === t.expertise.items.length - 1;
              return (
                <div key={item.title} style={{ animationDelay: `${i * 80}ms` }}
                  className={`reveal group rounded-xl border border-neutral-100 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${isLast ? "md:col-span-2 md:mx-auto md:max-w-[calc(50%-12px)]" : ""}`}>
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-[#F26522]/10 text-[#F26522] transition-colors duration-300 group-hover:bg-[#F26522] group-hover:text-white">
                    {Icon && <Icon className="h-6 w-6" />}
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900">{item.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-neutral-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-6 py-20 lg:py-28">
        <div className="reveal mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-neutral-900 lg:text-4xl">{t.team.title}</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-neutral-600">{t.team.intro}</p>
        </div>
        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {t.team.members.map((m, i) => (
            <div key={m.slug} style={{ animationDelay: `${i * 90}ms` }}
              onClick={() => navigate(lp(`/team/${m.slug}`))}
              className="reveal group cursor-pointer overflow-hidden rounded-xl border border-neutral-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={m.image} alt={m.name} className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-neutral-900 transition-colors group-hover:text-[#F26522]">{m.name}</h3>
                <p className="mt-1 text-sm font-medium text-[#F26522]">{t.team.role}</p>
                <p className="mt-4 border-l-2 border-neutral-200 pl-4 text-sm italic leading-relaxed text-neutral-600 line-clamp-5">“{m.quote}”</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <button onClick={() => navigate(lp("/team"))}
            className="group inline-flex items-center gap-2 rounded-md border border-neutral-300 px-7 py-3 text-sm font-semibold text-neutral-800 transition-all duration-300 hover:border-[#F26522] hover:text-[#F26522]">
            {t.team.viewAll}
            <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      <section className="bg-[#0f1a10] py-24">
        <div className="reveal mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold text-white lg:text-4xl">{t.commitment.title}</h2>
          <p className="mt-6 text-lg leading-relaxed text-neutral-300">{t.commitment.text}</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-6 py-20 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="reveal">
            <h2 className="text-3xl font-bold text-neutral-900 lg:text-4xl">{t.contact.title}</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-neutral-600">{t.contact.intro}</p>
            <div className="mt-8 space-y-5 text-sm text-neutral-700">
              <p><span className="font-semibold">{t.contact.labels.email}:</span>{" "}
                <a href={`mailto:${t.contact.email}`} className="text-[#F26522] hover:underline">{t.contact.email}</a>
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {t.contact.offices.map((o) => (
                  <div key={o.city}>
                    <div className="font-semibold text-neutral-900">{o.city}</div>
                    <div className="mt-1 space-y-0.5 text-neutral-600">
                      {o.lines.map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="reveal rounded-xl border border-neutral-100 bg-white p-8 shadow-sm">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
